import type { DotLottie } from '@lottiefiles/dotlottie-react';
import { useEffect, useState } from 'react';
import { useResolvedTheme } from '../../theme/useResolvedTheme';
import { parseToNormalizedRgb } from '../../utils/colorUtils';
import './initWasm';

/** Color slot id every recolorable `.lottie` in `src/animations` binds its visible fills/strokes to. */
const LOTTIE_COLOR_SLOT = 'main';

/**
 * Paints the {@link LOTTIE_COLOR_SLOT} color slot of a `.lottie` animation.
 *
 * The color is re-applied on every `load` and whenever `color` or the resolved light/dark theme changes,
 * without restarting playback. The `themeData` prop of `DotLottieReact` is not used on purpose: it is applied
 * only on prop change, before the WASM player is loaded, so the initial value is dropped.
 *
 * @param color any CSS color — hex, `rgb()`, named or a versioned theme color from `getSemanticColors(version)`;
 * variables are resolved against the current theme. When omitted the animation keeps its original colors.
 * @returns ref callback to pass as `dotLottieRefCallback` to `DotLottieReact`
 */
export const useLottieColorSlot = (color?: string) => {
    const theme = useResolvedTheme();
    const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);

    useEffect(() => {
        const rgb = color ? parseToNormalizedRgb(color, theme) : null;
        if (!dotLottie || !rgb) return;

        const apply = () => dotLottie.setColorSlot(LOTTIE_COLOR_SLOT, [...rgb, 1]);
        if (dotLottie.isLoaded) apply();
        dotLottie.addEventListener('load', apply);
        return () => dotLottie.removeEventListener('load', apply);
    }, [dotLottie, color, theme]);

    return setDotLottie;
};
